import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { TextField } from "../../../../components/text-field";
import { ImageUploadCropper } from "../../../../components/image-upload-cropper";
import { Label, RichTextEditor } from "../../../../components";
import {
  MAX_NEWS_TITLE_LENGTH,
  createNewsFormSchema,
  getNewsThumbnailError,
  toNewsThumbnailErrorMessage,
  type NewsFormSubmitValues,
  type NewsFormValidationMessages,
  type NewsFormValues,
} from "../../schemas/news.schema";

export type { NewsFormSubmitValues, NewsFormValues };

const ACCEPTED_THUMBNAIL_TYPES = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
};


interface NewsFormProps {
  formId: string;
  loading?: boolean;
  error?: string | null;
  defaultValues?: Partial<NewsFormValues>;
  onUploadStateChange?: (uploading: boolean) => void;
  onSubmit: (values: NewsFormSubmitValues) => Promise<void>;
}

export default function NewsForm({
  formId,
  loading = false,
  error,
  defaultValues,
  onUploadStateChange,
  onSubmit,
}: NewsFormProps) {
  const { t } = useTranslation();
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);

  const validationMessages = useMemo<NewsFormValidationMessages>(
    () => ({
      titleRequired: `${t("news.title")} is required.`,
      titleTooLong: `${t("news.title")} must be at most ${MAX_NEWS_TITLE_LENGTH} characters.`,
      contentRequired: `${t("news.content")} is required.`,
      thumbnailTooLarge: `${t("news.thumbnail")} ${t("news.max_size_5mb")}`,
      thumbnailInvalidType: `${t("news.thumbnail_hint")} JPG, PNG, GIF, WebP.`,
    }),
    [t],
  );

  const formSchema = useMemo(
    () => createNewsFormSchema(validationMessages),
    [validationMessages],
  );

  const { control, setValue, reset, handleSubmit } = useForm<NewsFormValues>({
    mode: "onBlur",
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: defaultValues?.title ?? "",
      content: defaultValues?.content ?? "",
      location: defaultValues?.location ?? "",
      thumbnail_url: defaultValues?.thumbnail_url ?? "",
    },
  });

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    setThumbnailFile(null);
    setThumbnailError(null);
    reset({
      title: defaultValues.title ?? "",
      content: defaultValues.content ?? "",
      location: defaultValues.location ?? "",
      thumbnail_url: defaultValues.thumbnail_url ?? "",
    });
  }, [defaultValues, reset]);

  const handleThumbnailChange = async (file: File | null) => {
    if (file) {
      const thumbnailIssue = getNewsThumbnailError(file);

      if (thumbnailIssue) {
        setThumbnailError(toNewsThumbnailErrorMessage(thumbnailIssue, validationMessages));
        onUploadStateChange?.(false);
        return;
      }
    }

    setThumbnailError(null);
    setThumbnailFile(file);
    setValue("thumbnail_url", file ? "" : (defaultValues?.thumbnail_url ?? ""), {
      shouldDirty: true,
    });
    onUploadStateChange?.(Boolean(file));
  };

  const submitForm = handleSubmit(async (values) => {
    if (loading) {
      return;
    }

    if (thumbnailFile) {
      const thumbnailIssue = getNewsThumbnailError(thumbnailFile);

      if (thumbnailIssue) {
        setThumbnailError(toNewsThumbnailErrorMessage(thumbnailIssue, validationMessages));
        return;
      }
    }

    await onSubmit({
      ...values,
      thumbnail_file: thumbnailFile,
    });
  });

  return (
    <form
      id={formId}
      onSubmit={(event) => void submitForm(event)}
      className="mx-auto max-w-[1440px] space-y-10 px-6 py-10 md:px-12 md:py-14"
    >
      {error ? (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {error}
        </div>
      ) : null}

      <div className="space-y-3">
        <Label className="font-bold">{t("news.thumbnail")}</Label>
        <ImageUploadCropper
          value={thumbnailFile || defaultValues?.thumbnail_url || null}
          onChange={(file) => void handleThumbnailChange(file)}
          aspect={2.3}
          title={t("news.thumbnail")}
          description={t("news.thumbnail_hint")}
          disabled={loading}
          accept={ACCEPTED_THUMBNAIL_TYPES}
          outputFileName="news_thumbnail.jpg"
        />
        <p className="text-sm text-slate-500">
          {t("news.thumbnail_hint")} JPG, PNG, GIF, WebP. {t("news.max_size_5mb")}
        </p>
        {thumbnailError ? <p className="text-sm text-red-500">{thumbnailError}</p> : null}
      </div>

      <div className="flex flex-col gap-2">
        <Label
          htmlFor="news-title"
          className="font-bold"
        >
          {t("news.title")} <span aria-hidden="true">*</span>
        </Label>
        <Controller
          control={control}
          name="title"
          render={({ field, fieldState }) => (
            <TextField
              id="news-title"
              value={field.value || ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              placeholder={t("news.enter_news_title")}
              className="dark:border-gray-400"
              inputClassName="dark:bg-gray-800 dark:text-white"
              disabled={loading}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label
          htmlFor="news-location"
          className="font-bold"
        >
          {t("news.location")}
        </Label>
        <Controller
          control={control}
          name="location"
          render={({ field }) => (
            <TextField
              id="news-location"
              value={field.value || ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              placeholder={t("news.enter_news_location")}
              className="dark:border-gray-400"
              inputClassName="dark:bg-gray-800 dark:text-white"
              disabled={loading}
              fullWidth
            />
          )}
        />
      </div>

      <div className="space-y-3">
        <Label className="font-bold">
          {t("news.content")} <span aria-hidden="true">*</span>
        </Label>
        <Controller
          name="content"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <RichTextEditor
                value={field.value}
                onChange={field.onChange}
                placeholder={t("news.enter_news_content")}
                disabled={loading}
                className="min-h-[520px] text-lg [&_[contenteditable]]:!min-h-[420px]"
                error={Boolean(fieldState.error)}
              />
              {fieldState.error ? (
                <p className="text-sm text-red-500">{fieldState.error.message}</p>
              ) : null}
            </>
          )}
        />
        <p className="text-sm text-slate-500">{t("news.content_editor_hint")}</p>
      </div>
    </form>
  );
}
